import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import { StyledAppSelect } from '@nxsys/components/AppSelect/index.styled';
import {
  defaultPaymentRateTypeOptions,
  defaultUnits,
} from '@nxsys/constants/AppConstants';
import { StatusEnum } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Option } from '@nxsys/types/models/Common';
import { getVATCodeOption } from '@nxsys/utils';
import { Alert, Form, Radio } from 'antd';
import React, { Ref, useImperativeHandle, useMemo } from 'react';
import { useSelector } from 'react-redux';
import AppRateInput from '@nxsys/components/AppRateInput';

export type PaymentRateFormValues = {
  code: string;
  defaultVATCode: string;
  description: string;
  displayOnPayslip: boolean;
  hoursPerUnit: string;
  includeAccrueHolidayPay: boolean;
  excludeFromNmwCalculation: boolean;
  includeInCisCalculation: boolean;
  includeNICalculation: boolean;
  includePensionCalculation: boolean;
  includeTaxCalculation: boolean;
  raiseInvoice: boolean;
  status: string;
  type: Option<string> | null;
  units: string;
  taxCanBeDeducted?: boolean;
  employeePRSICanBeDeducted?: boolean;
  employerPRSICanBeDeducted?: boolean;
  uscCanBeDeducted?: boolean;
  ascCanBeDeducted?: boolean;
  pensionSchemaDeductionCanBeDeducted?: boolean;
  lptCanBeDeducted?: boolean;
};

export type PaymentRateFormRef = {
  submit: () => void;
};

type PaymentRateFormProps = {
  formRef?: Ref<PaymentRateFormRef>;
  onSubmit?: (values: PaymentRateFormValues) => void;
  defaultValues?: Partial<PaymentRateFormValues>;
  // NX-6038: a default rate's flags are copied onto every payment item made
  // from it, so the API rejects any change but the description
  onlyDescriptionEditable?: boolean;
};

const PaymentRateForm: React.FC<PaymentRateFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
  onlyDescriptionEditable,
}) => {
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const { vatCodeList } = useSelector(
    (state: AppState) => state.paymentSettingStore,
  );
  const { list: vatCodeListData } = vatCodeList;
  const vatCodeListOptions = useMemo(
    () => vatCodeListData.map((item) => getVATCodeOption(item)),
    [vatCodeListData],
  );

  const { messages } = useTypedIntl();

  const [form] = Form.useForm<PaymentRateFormValues>();
  // NX-6007: a Cost of Materials rate hides its tax, NI, pension, holiday
  // pay, NMW and CIS options. `hidden` keeps the fields in the form, so saving
  // keeps the values they already hold.
  const isCostOfMaterials =
    Form.useWatch('type', form)?.value === 'COST_OF_MATERIALS';

  useImperativeHandle(formRef, () => ({
    submit() {
      // the locked fields keep their required rules, so an empty one (e.g.
      // Hours / Unit on Holiday Pay) would block saving the description
      if (onlyDescriptionEditable) {
        form
          .validateFields(['description'])
          .then(() => onFinish(form.getFieldsValue(true)))
          .catch(onFinishFailed);
        return;
      }
      form.submit();
    },
  }));

  const onFinish = (values: PaymentRateFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      disabled={onlyDescriptionEditable}
      {...(defaultValues ? { initialValues: defaultValues } : {})}
      initialValues={{
        ...defaultValues,
        ...(isBureau
          ? {
              type: defaultPaymentRateTypeOptions.find(
                (item) => item.value === 'RATE',
              ),
            }
          : {}),
      }}
    >
      {onlyDescriptionEditable && (
        <Alert
          className="mb-3"
          message={
            messages[
              'paymentSetting.paymentRate.defaultRateOnlyDescription'
            ] as string
          }
          type="info"
          showIcon
        />
      )}

      <Form.Item
        label={messages['paymentSetting.paymentRate.code']}
        name="code"
        required={true}
      >
        <AppInput placeholder={messages['form.pleaseEnter']} />
      </Form.Item>

      <Form.Item
        label={messages['paymentSetting.payment.description']}
        name="description"
        rules={[
          {
            required: true,
            message: messages['form.pleaseEnter'],
          },
        ]}
      >
        <AppInput
          placeholder={messages['form.enterDescription']}
          disabled={false}
        />
      </Form.Item>

      <Form.Item
        label={messages['common.type']}
        name="type"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <AppSelect
          options={defaultPaymentRateTypeOptions}
          placeholder={messages['form.select']}
          size="large"
          showSearch
          disabled={isBureau || onlyDescriptionEditable}
        />
      </Form.Item>

      <Form.Item
        label={messages['common.units']}
        name="units"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <StyledAppSelect
          options={defaultUnits}
          placeholder={messages['form.select']}
          size="large"
          showSearch
        />
      </Form.Item>

      <Form.Item
        label={`${messages['common.hours']} / ${messages['common.unit']}`}
        name="hoursPerUnit"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <AppRateInput placeholder={messages['form.pleaseEnter']} />
      </Form.Item>

      <Form.Item
        label={messages['paymentSetting.paymentRate.defaultVATCode']}
        name="defaultVATCode"
      >
        <StyledAppSelect
          options={vatCodeListOptions}
          placeholder={messages['form.select']}
          size="large"
          showSearch
        />
      </Form.Item>

      <Form.Item
        label={messages['common.status']}
        name="status"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <Radio.Group>
          <Radio value={StatusEnum.ACTIVE}>{messages['common.active']}</Radio>
          <Radio value={StatusEnum.INACTIVE}>
            {messages['common.inactive']}
          </Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label={messages['paymentSetting.payment.displayOnPayslip']}
        name="displayOnPayslip"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <Radio.Group>
          <Radio value={true}>{messages['common.yes']}</Radio>
          <Radio value={false}>{messages['common.no']}</Radio>
        </Radio.Group>
      </Form.Item>

      {!isIreland ? (
        <>
          {!isBureau && (
            <Form.Item
              label={messages['paymentSetting.paymentRate.beforeCisTax']}
              name="includeInCisCalculation"
              hidden={isCostOfMaterials}
              rules={[
                {
                  required: true,
                  message: messages['form.pleaseSelect'],
                },
              ]}
            >
              <Radio.Group>
                <Radio value={true}>{messages['common.yes']}</Radio>
                <Radio value={false}>{messages['common.no']}</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          <Form.Item
            label={messages['paymentSetting.payment.beforeTax']}
            name="includeTaxCalculation"
            hidden={isCostOfMaterials}
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={messages['paymentSetting.payment.beforeNI']}
            name="includeNICalculation"
            hidden={isCostOfMaterials}
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={messages['paymentSetting.payment.beforePension']}
            name="includePensionCalculation"
            hidden={isCostOfMaterials}
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={messages['paymentSetting.paymentRate.accrueHolidayPay']}
            name="includeAccrueHolidayPay"
            hidden={isCostOfMaterials}
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={messages['paymentSetting.paymentRate.excludeFromNmwCalculation']}
            name="excludeFromNmwCalculation"
            hidden={isCostOfMaterials}
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>

          {!isBureau && (
            <Form.Item
              label={messages['paymentSetting.paymentRate.raiseInvoice']}
              name="raiseInvoice"
              rules={[
                {
                  required: true,
                  message: messages['form.pleaseSelect'],
                },
              ]}
            >
              <Radio.Group>
                <Radio value={true}>{messages['common.yes']}</Radio>
                <Radio value={false}>{messages['common.no']}</Radio>
              </Radio.Group>
            </Form.Item>
          )}
        </>
      ) : (
        <>
          <Form.Item
            label={messages['paymentSetting.payment.beforeTax']}
            name="taxCanBeDeducted"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={messages['paymentSetting.payment.beforeEmployeePRSI']}
            name="employeePRSICanBeDeducted"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={messages['paymentSetting.payment.beforeEmployerPRSI']}
            name="employerPRSICanBeDeducted"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={messages['paymentSetting.payment.beforeUSC']}
            name="uscCanBeDeducted"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={messages['paymentSetting.payment.beforeASC']}
            name="ascCanBeDeducted"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={
              messages['paymentSetting.payment.beforePensionSchemeDeductions']
            }
            name="pensionSchemaDeductionCanBeDeducted"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={messages['paymentSetting.payment.beforeLPT']}
            name="lptCanBeDeducted"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={messages['paymentSetting.paymentRate.accrueHolidayPay']}
            name="includeAccrueHolidayPay"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={messages['paymentSetting.paymentRate.excludeFromNmwCalculation']}
            name="excludeFromNmwCalculation"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={messages['paymentSetting.payment.beforeRCT']}
            name="includeInCisCalculation"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
        </>
      )}
    </AppFormContainer>
  );
};

export default PaymentRateForm;
